// Client-side reporting metrics computed from the task list (which already
// carries completedAt / createdAt / status / type / assigneeIds). No server
// work needed for throughput, lead time, and completion trend.

import { parseDateLocal } from "./format";

type ReportTask = {
  id?: string;
  title?: string;
  status: string;
  type?: string | null;
  assigneeId?: string | null;
  assigneeIds?: string[];
  createdAt: string;
  completedAt?: string | null;
  startedAt?: string | null;
};

const DAY = 86_400_000;

/** Monday-anchored week start (local) for a date. */
export function weekStart(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (x.getDay() + 6) % 7; // 0 = Monday
  x.setDate(x.getDate() - dow);
  return x;
}

export function weekKey(d: Date): string {
  const w = weekStart(d);
  return `${w.getFullYear()}-${String(w.getMonth() + 1).padStart(2, "0")}-${String(w.getDate()).padStart(2, "0")}`;
}

/** The last `n` week-start keys ending at the current week, oldest first. */
export function lastNWeeks(n: number, now = new Date()): string[] {
  const start = weekStart(now);
  const keys: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(start);
    d.setDate(start.getDate() - i * 7);
    keys.push(weekKey(d));
  }
  return keys;
}

/**
 * Tasks completed per week over the last `weeks`, optionally split by a
 * dimension (assignee | type). Returns the ordered week keys + per-series
 * counts.
 */
export function throughput(
  tasks: ReportTask[],
  weeks: number,
  breakdown: "none" | "assignee" | "type",
  now = new Date(),
): { weeks: string[]; series: { key: string; counts: number[] }[] } {
  const keys = lastNWeeks(weeks, now);
  const idx = new Map(keys.map((k, i) => [k, i]));
  const series = new Map<string, number[]>();
  const ensure = (k: string) => {
    if (!series.has(k)) series.set(k, new Array(keys.length).fill(0));
    return series.get(k)!;
  };
  for (const t of tasks) {
    if (t.status !== "done" || !t.completedAt) continue;
    const d = parseDateLocal(t.completedAt.slice(0, 10));
    if (!d) continue;
    const wi = idx.get(weekKey(d));
    if (wi == null) continue;
    let k = "All";
    if (breakdown === "type") k = t.type ?? "task";
    else if (breakdown === "assignee")
      k = t.assigneeIds?.[0] ?? t.assigneeId ?? "Unassigned";
    const arr = ensure(k);
    arr[wi] = (arr[wi] ?? 0) + 1;
  }
  if (series.size === 0) series.set("All", new Array(keys.length).fill(0));
  return {
    weeks: keys,
    series: [...series.entries()].map(([key, counts]) => ({ key, counts })),
  };
}

/** Centered-trailing rolling average of a numeric series. */
export function rollingAverage(values: number[], window: number): number[] {
  return values.map((_, i) => {
    const lo = Math.max(0, i - window + 1);
    const slice = values.slice(lo, i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}

/** Lead time (creation → completion) in days for each completed task. */
export function leadTimes(tasks: ReportTask[]): number[] {
  const out: number[] = [];
  for (const t of tasks) {
    if (t.status !== "done" || !t.completedAt) continue;
    const c = parseDateLocal(t.createdAt.slice(0, 10));
    const done = parseDateLocal(t.completedAt.slice(0, 10));
    if (c && done) out.push(Math.max(0, (done.getTime() - c.getTime()) / DAY));
  }
  return out;
}

export function average(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
}

/**
 * Cycle time (doing → done) in days per completed task that has a recorded
 * start. Returns id/title so the page can link outliers, sorted longest-first.
 */
export function cycleTimes(
  tasks: ReportTask[],
): { id: string; title: string; days: number }[] {
  const out: { id: string; title: string; days: number }[] = [];
  for (const t of tasks) {
    if (t.status !== "done" || !t.completedAt || !t.startedAt) continue;
    const s = parseDateLocal(t.startedAt.slice(0, 10));
    const d = parseDateLocal(t.completedAt.slice(0, 10));
    if (s && d)
      out.push({
        id: t.id ?? "",
        title: t.title ?? "",
        days: Math.max(0, (d.getTime() - s.getTime()) / DAY),
      });
  }
  return out.sort((a, b) => b.days - a.days);
}

/** The p-th percentile (0–100) of a numeric sample (nearest-rank). */
export function percentile(xs: number[], p: number): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const rank = Math.ceil((p / 100) * s.length);
  return s[Math.min(s.length - 1, Math.max(0, rank - 1))]!;
}

/** Tasks created vs completed per week over the last `weeks`. */
export function createdVsCompleted(
  tasks: ReportTask[],
  weeks: number,
  now = new Date(),
): { weeks: string[]; created: number[]; completed: number[] } {
  const keys = lastNWeeks(weeks, now);
  const idx = new Map(keys.map((k, i) => [k, i]));
  const created = new Array(keys.length).fill(0);
  const completed = new Array(keys.length).fill(0);
  for (const t of tasks) {
    const cd = parseDateLocal(t.createdAt.slice(0, 10));
    if (cd) {
      const ci = idx.get(weekKey(cd));
      if (ci != null) created[ci] += 1;
    }
    if (t.status === "done" && t.completedAt) {
      const dd = parseDateLocal(t.completedAt.slice(0, 10));
      if (dd) {
        const di = idx.get(weekKey(dd));
        if (di != null) completed[di] += 1;
      }
    }
  }
  return { weeks: keys, created, completed };
}

/**
 * Naive projected completion: weeks-to-clear the open backlog at the recent
 * average weekly throughput. Returns null when nothing is open or throughput
 * is zero (can't project).
 */
export function projectedCompletion(
  tasks: ReportTask[],
  recentThroughputPerWeek: number,
  now = new Date(),
): { weeks: number; date: string } | null {
  const open = tasks.filter((t) => t.status !== "done").length;
  if (open === 0 || recentThroughputPerWeek <= 0) return null;
  const weeks = Math.ceil(open / recentThroughputPerWeek);
  const d = new Date(now);
  d.setDate(d.getDate() + weeks * 7);
  return { weeks, date: d.toISOString().slice(0, 10) };
}

/**
 * Average lead time and average cycle time of tasks *completed* in each of the
 * last `weeks` weeks — the data for a lead-vs-cycle trend comparison.
 */
export function leadCycleByWeek(
  tasks: ReportTask[],
  weeks: number,
  now = new Date(),
): { weeks: string[]; lead: number[]; cycle: number[] } {
  const keys = lastNWeeks(weeks, now);
  const idx = new Map(keys.map((k, i) => [k, i]));
  const leadSum = new Array(keys.length).fill(0);
  const leadN = new Array(keys.length).fill(0);
  const cycleSum = new Array(keys.length).fill(0);
  const cycleN = new Array(keys.length).fill(0);
  for (const t of tasks) {
    if (t.status !== "done" || !t.completedAt) continue;
    const done = parseDateLocal(t.completedAt.slice(0, 10));
    if (!done) continue;
    const wi = idx.get(weekKey(done));
    if (wi == null) continue;
    const created = parseDateLocal(t.createdAt.slice(0, 10));
    if (created) {
      leadSum[wi] += (done.getTime() - created.getTime()) / DAY;
      leadN[wi] += 1;
    }
    if (t.startedAt) {
      const started = parseDateLocal(t.startedAt.slice(0, 10));
      if (started) {
        cycleSum[wi] += (done.getTime() - started.getTime()) / DAY;
        cycleN[wi] += 1;
      }
    }
  }
  return {
    weeks: keys,
    lead: keys.map((_, i) => (leadN[i] ? leadSum[i] / leadN[i] : 0)),
    cycle: keys.map((_, i) => (cycleN[i] ? cycleSum[i] / cycleN[i] : 0)),
  };
}

/**
 * Burn-up data over the last `weeks` weeks. For each week boundary:
 *  - `scope` = cumulative tasks that existed by the end of that week (created
 *    on or before it, including tasks predating the window),
 *  - `done`  = cumulative tasks completed by the end of that week.
 * The gap between the two lines is remaining work (burn-down read inverted).
 * `scopeAdded[i]` counts tasks created *within* week i — non-zero entries are
 * the scope-change markers.
 */
export function burnUp(
  tasks: ReportTask[],
  weeks: number,
  now = new Date(),
): { weeks: string[]; scope: number[]; done: number[]; scopeAdded: number[] } {
  const keys = lastNWeeks(weeks, now);
  const idx = new Map(keys.map((k, i) => [k, i]));
  // End-of-week boundary (exclusive) for each week key.
  const weekEnds = keys.map((k) => {
    const s = parseDateLocal(k)!;
    return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 7).getTime();
  });
  const scope = new Array(keys.length).fill(0);
  const done = new Array(keys.length).fill(0);
  const scopeAdded = new Array(keys.length).fill(0);
  for (const t of tasks) {
    const cd = parseDateLocal(t.createdAt.slice(0, 10));
    const dd =
      t.status === "done" && t.completedAt
        ? parseDateLocal(t.completedAt.slice(0, 10))
        : null;
    for (let i = 0; i < keys.length; i++) {
      const end = weekEnds[i]!;
      if (cd && cd.getTime() < end) scope[i] += 1;
      if (dd && dd.getTime() < end) done[i] += 1;
    }
    if (cd) {
      const ai = idx.get(weekKey(cd));
      if (ai != null) scopeAdded[ai] += 1;
    }
  }
  return { weeks: keys, scope, done, scopeAdded };
}

/** Cumulative completed-task count by week over the last `weeks`. */
export function completionTrend(
  tasks: ReportTask[],
  weeks: number,
  now = new Date(),
): { weeks: string[]; cumulative: number[] } {
  const { weeks: keys, series } = throughput(tasks, weeks, "none", now);
  const perWeek = series[0]?.counts ?? new Array(keys.length).fill(0);
  let running = 0;
  return { weeks: keys, cumulative: perWeek.map((c) => (running += c)) };
}
